import {
  Query,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { ErrorType } from '@/features/auth/axios-config'
import {
  EDocumentFilterOnly,
  ErrorResponse,
  RequestFulltextSearchDocument,
  ResponseDocumentRetrieval,
} from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import {
  DEFAULT_PAGE,
  DEFAULT_STALE_TIME_MS,
  MIN_SEARCH_LENGTH,
  TABLE_PAGE_SIZE,
} from '@/utils/constants'

import { DOCUMENT_LIST_QUERY_KEY } from './constants'
import {
  addBookmark,
  deleteDocument,
  getDocumentList,
  getDocumentRetrieval,
  getDocumentVersion,
  getVersionList,
  removeBookmark,
  searchDocuments,
  updateDocument,
} from './services'
import { useDocumentStore } from './store'
import { DocumentFilters, UpdateDocument } from './types'

export const useDocumentList = (params: {
  deliverableSetId: string
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  filters?: DocumentFilters
  filterOnly?: EDocumentFilterOnly | null
}) => {
  const {
    deliverableSetId,
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    filters,
    filterOnly,
  } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''
  const sortBy = sort ? convertSortToApiFormat(sort) : ''
  const filterBy: Record<string, string[]> = {}
  filterBy.deliverable_set_id = [deliverableSetId]
  if (filters?.statuses && filters?.statuses?.length > 0) {
    filterBy.status = filters?.statuses || []
  }
  if (filters?.type && filters?.type !== 'All') {
    filterBy.document_type = [filters.type]
  }

  return useQuery({
    queryKey: DOCUMENT_LIST_QUERY_KEY([
      deliverableSetId,
      query,
      page,
      rowsPerPage,
      sortBy,
      JSON.stringify(filters),
      filterOnly ?? '',
    ]),
    queryFn: async () => {
      const { data, pagination } = await getDocumentList({
        q: query,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy || undefined,
        filter_by: Object.keys(filterBy).length > 0 ? filterBy : undefined,
        filter_only: filterOnly || undefined,
      })
      return { data, pagination }
    },
    enabled: !!deliverableSetId, // Only run query if deliverableSetId is provided
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchOnMount: 'always',
  })
}

export const useDocumentsList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  filters?: DocumentFilters
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    filters,
  } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''
  const sortBy = sort ? convertSortToApiFormat(sort) : ''
  const filterBy: Record<string, string[]> = {}

  if (filters?.statuses && filters?.statuses?.length > 0) {
    filterBy.status = filters?.statuses || []
  }
  if (filters?.type && filters?.type !== 'All') {
    filterBy.document_type = [filters.type]
  }

  return useQuery({
    queryKey: [
      'all-documents',
      query,
      page,
      rowsPerPage,
      sortBy,
      JSON.stringify(filters),
    ],
    queryFn: async () => {
      const { data, pagination } = await getDocumentList({
        q: query,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        ...(sortBy && { sort_by: sortBy }),
        ...(Object.keys(filterBy).length && { filter_by: filterBy }),
      })
      return { data, pagination }
    },
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchOnMount: 'always',
  })
}

export const useDocumentRetrieval = (documentId: string) => {
  return useQuery({
    queryKey: ['document-retrieval', documentId],
    queryFn: async () => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await getDocumentRetrieval(documentId)
      return response.data?.[0]
    },
    enabled: !!documentId, // Only run query if documentId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Delete a document
 * @param refetchQueries - Whether to refetch the document list
 * @returns Mutation function to delete a document
 */
export const useDeleteDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      documentId: string
      refetchQueries: boolean
    }) => {
      const { documentId, refetchQueries = false } = payload

      await deleteDocument(documentId)
      if (refetchQueries) {
        queryClient.invalidateQueries({
          queryKey: DOCUMENT_LIST_QUERY_KEY(),
          refetchType: 'active',
        })
      }
    },
  })
}

export const useUpdateDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      documentId,
      data,
    }: {
      documentId: string
      data: UpdateDocument
    }) => {
      const response = await updateDocument(documentId, data)
      return response.data?.[0]
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_LIST_QUERY_KEY(),
        refetchType: 'active',
      })
      if (data.document_id) {
        queryClient.invalidateQueries({
          queryKey: ['document-version', data.document_id, data.version_id],
          refetchType: 'active',
        })
      }
    },
  })
}

export const useVersionList = (documentId: string) => {
  return useQuery({
    queryKey: ['version-list', documentId],
    queryFn: async () => {
      const response = await getVersionList(documentId)
      return response.data
    },
    enabled: !!documentId,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchOnMount: 'always',
  })
}

export const useDocumentVersion = <TError = ErrorType<ErrorResponse>>(
  documentId: string,
  versionId: string,
  refetchInterval?: (
    query: Query<
      ResponseDocumentRetrieval,
      TError,
      ResponseDocumentRetrieval,
      readonly unknown[]
    >
  ) => number | false | undefined
) => {
  return useQuery({
    queryKey: ['document-version', documentId, versionId],
    queryFn: async () => {
      const response = await getDocumentVersion(documentId, versionId)
      return response.data?.[0]
    },
    enabled: !!documentId && !!versionId,
    refetchInterval: refetchInterval
      ? (query) =>
          refetchInterval(
            query as unknown as Query<
              ResponseDocumentRetrieval,
              TError,
              ResponseDocumentRetrieval,
              readonly unknown[]
            >
          )
      : undefined,
  })
}

/**
 * @summary Search documents
 */
export const useSearchDocuments = <TError = ErrorType<ErrorResponse>>(params: {
  deliverableSetId: string
  searchQuery: string
  limit?: number
  tflType?: RequestFulltextSearchDocument['tfl_type']
  enabled?: boolean
  staleTime?: number
  refetchOnWindowFocus?: 'always' | boolean
  refetchOnReconnect?: 'always' | boolean
  initialPageParam?: number
}) => {
  const {
    deliverableSetId,
    searchQuery,
    limit = TABLE_PAGE_SIZE,
    tflType,
    enabled = true,
    staleTime = DEFAULT_STALE_TIME_MS,
    refetchOnWindowFocus = 'always',
    refetchOnReconnect = 'always',
    initialPageParam = DEFAULT_PAGE,
  } = params

  return useInfiniteQuery<Awaited<ReturnType<typeof searchDocuments>>, TError>({
    queryKey: [
      'search-documents',
      deliverableSetId,
      searchQuery,
      limit,
      tflType ?? '',
    ],
    queryFn: ({ pageParam, signal }) => {
      const offset =
        typeof pageParam === 'number' ? pageParam : initialPageParam
      return searchDocuments(
        {
          deliverable_set_id: deliverableSetId,
          q: searchQuery,
          limit,
          offset,
          ...(tflType ? { tfl_type: tflType } : {}),
        },
        undefined,
        signal
      )
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const pageSize = lastPage.pagination?.page_size ?? limit
      const hasNext =
        lastPage.pagination?.has_next ??
        (lastPage.data?.length ?? 0) >= pageSize

      const previousOffset =
        typeof lastPageParam === 'number' ? lastPageParam : 0
      return hasNext ? previousOffset + pageSize : undefined
    },
    initialPageParam,
    enabled: enabled && !!deliverableSetId && !!searchQuery,
    staleTime,
    refetchOnWindowFocus,
    refetchOnReconnect,
  })
}

export const useAddBookmark = () => {
  const queryClient = useQueryClient()
  const getVersionId = useDocumentStore((state) => state.getVersionId)

  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await addBookmark(documentId)
      return response.data?.[0]
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_LIST_QUERY_KEY(),
        refetchType: 'active',
      })
      if (data.document_id) {
        const versionId = getVersionId(data.document_id)
        if (versionId) {
          queryClient.invalidateQueries({
            queryKey: ['document-version', data.document_id, versionId],
            refetchType: 'active',
          })
        }
      }
    },
  })
}

export const useRemoveBookmark = () => {
  const queryClient = useQueryClient()
  const getVersionId = useDocumentStore((state) => state.getVersionId)

  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await removeBookmark(documentId)
      return response.data?.[0]
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_LIST_QUERY_KEY(),
        refetchType: 'active',
      })
      if (data.document_id) {
        const versionId = getVersionId(data.document_id)
        if (versionId) {
          queryClient.invalidateQueries({
            queryKey: ['document-version', data.document_id, versionId],
            refetchType: 'active',
          })
        }
      }
    },
  })
}
